import { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { useUpdatePost } from '@/lib/hooks/usePosts';

type EditPostDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId: number;
  initialContent: string;
};

export function EditPostDrawer({
  open,
  onOpenChange,
  postId,
  initialContent,
}: EditPostDrawerProps) {
  const [content, setContent] = useState(initialContent);
  const updatePost = useUpdatePost();
  const { toast, showToast, hideToast } = useToastNotification();

  // Reset content when drawer opens or initialContent changes
  useEffect(() => {
    if (open) {
      setContent(initialContent);
    }
  }, [open, initialContent]);

  const handleSubmit = async () => {
    if (!content.trim()) {
      return;
    }

    try {
      await updatePost.mutateAsync({
        postId,
        content: content.trim(),
      });
      // Show success toast
      showToast({
        title: 'Post updated',
        description: 'Your post has been updated successfully.',
        variant: 'success',
      });
      // Close drawer on success
      onOpenChange(false);
    } catch (error) {
      // Show error toast
      showToast({
        title: 'Update failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to update post. Please try again.',
        variant: 'error',
      });
      console.error('Failed to update post:', error);
    }
  };

  const isSubmitting = updatePost.isPending;
  const canSubmit = content.trim().length > 0 && !isSubmitting;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Edit Post</DrawerTitle>
        </DrawerHeader>
        <div className="py-4">
          <Textarea
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={isSubmitting}
            className="min-h-[120px] resize-none"
            autoFocus
          />
        </div>
        <DrawerFooter>
          <div className="flex gap-3">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex-1 h-14"
            >
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </Drawer>
  );
}


